import {useCallback, useRef, useState} from 'react';
import {CheckedValue} from '../../../../../../../components/ToggleButton/enum';
import {IAlert, IAlertConditionWithID} from '../../../../../actions/proto';
import {addIdToCondition} from '../../../utils';
import {transformConditionOperator} from './transformConditionOperator';
import {priority} from '@priority-software/priority-proto-api/lib';
import {RuleType} from '../../../../../enums';
import {autofillActionId, BusinessRuleAction} from '../../../../../constants';
import {showFormClientSideMessage} from '../../../../../../Form/actions/messages';
import {texts} from '../../../../../texts';
import {sendMonitor} from '../../../../../../../utils/monitoring';
import {checkingNewEmailTemplateSize, getMonitorMessage} from '../utils';

type HookProps = {
    session: string;
    rule: IAlert;
    type: RuleType;
    saveRule: (
        rule: priority.netitems.form.IAlert,
        type: priority.netitems.form.RuleType,
        onSuccess?: () => void
    ) => Promise<void>;
    closeSingleRuleDialog: () => void;
    onRuleUpdate: (data: Partial<IAlert>) => void;
    closeCurrentAutomation: () => void;
    isAIRule?: boolean;
    newEditorSupport?: boolean;
};

export type UseRuleContentAPI = {
    handlers: {
        addNewCondition: (condition: IAlertConditionWithID) => void;
        onChangeCondition: (id: string, data: Partial<IAlertConditionWithID>) => void;
        removeCondition: (id: string) => void;
        toggleConditionOperator: (checked: CheckedValue) => void;
        toggleActive: () => void;
        onRuleSave: () => Promise<void>;
        onBack: () => void;
        handleRuleFormData: (data: Partial<priority.netitems.form.IAlertForm>) => void;
        toggleHasTemporaryEmailTemplate: (flag: boolean) => void;
    };
    state: {
        isRuleActive: boolean;
        conditionOperator: CheckedValue;
        conditions: Array<IAlertConditionWithID>;
        hasTemporaryEmailTemplate: boolean;
    };
};

export const useRuleContent = (props: HookProps): UseRuleContentAPI => {
    const {
        rule,
        type,
        session = '',
        saveRule,
        closeSingleRuleDialog,
        onRuleUpdate,
        newEditorSupport,
        isAIRule
    } = props;
    const initialDataRef = useRef(rule);
    const isRuleChanged = initialDataRef.current !== rule;

    const [conditions, setConditions] = useState(addIdToCondition(rule.AlertConds));
    const [isRuleActive, setIsRuleActive] = useState(rule.Active || false);
    const [conditionOperator, setConditionOperator] = useState<CheckedValue>(
        transformConditionOperator(rule.ConditionsOperator)
    );
    const [hasTemporaryEmailTemplate, toggleHasTemporaryEmailTemplate] = useState<boolean>(false);

    const handleRuleFormData = useCallback(
        (value: Partial<priority.netitems.form.IAlertForm>) => {
            onRuleUpdate({
                Form: {
                    ...(rule?.Form || {}),
                    ...value
                }
            });
        },
        [onRuleUpdate, rule]
    );

    const addNewCondition = useCallback(
        (newCondition: IAlertConditionWithID) => {
            const updatedConditions = [...conditions, newCondition];
            setConditions(updatedConditions);
            onRuleUpdate({AlertConds: updatedConditions});
        },
        [onRuleUpdate, conditions]
    );

    const onChangeCondition = useCallback(
        (id: string, data: Partial<IAlertConditionWithID>) => {
            const updatedData = conditions.map(item =>
                item.id === id ? {...item, ...data} : item
            );
            setConditions(updatedData);
            onRuleUpdate({AlertConds: updatedData});
        },
        [onRuleUpdate, conditions]
    );

    const removeCondition = useCallback(
        (id: string) => {
            const updatedConditions = conditions.filter(condition => condition.id !== id);

            setConditions(updatedConditions);
            onRuleUpdate({AlertConds: updatedConditions});
        },
        [onRuleUpdate, conditions]
    );

    const toggleActive = useCallback(() => {
        onRuleUpdate({Active: !isRuleActive});
        setIsRuleActive(!isRuleActive);
    }, [isRuleActive]);

    const toggleConditionOperator = useCallback(
        (checked: CheckedValue) => {
            setConditionOperator(checked);

            onRuleUpdate({ConditionsOperator: checked === CheckedValue.right});
        },
        [onRuleUpdate]
    );

    const onRuleSave = useCallback(async () => {
        const {isContinue: isEmailSizeValid, CoverLetters: updatedCoverLetters} =
            await checkingNewEmailTemplateSize(rule.CoverLetters);
        if (!isEmailSizeValid) {
            console.error(
                'Email template size exceeded the limit!!! Please reduce the size of attachments.'
            );
        }

        const shouldSaveCoverLetters =
            rule.MessageType === BusinessRuleAction.EmailMsg && newEditorSupport;

        const data: priority.netitems.form.IAlert = {
            ...rule,
            Messages: shouldSaveCoverLetters ? null : rule.Messages,
            CoverLetters: shouldSaveCoverLetters ? updatedCoverLetters : null
        };

        // Restore original messages if nothing was saved
        if (!data.Messages && (!data.CoverLetters || data.CoverLetters.length === 0)) {
            data.Messages = rule.Messages;
        }

        if (type === RuleType.business) {
            const monitorText = getMonitorMessage(data);
            if (monitorText) {
                sendMonitor(monitorText);
            }
        }

        if (type === RuleType.autofill && !data.MessageType) {
            data.MessageType = autofillActionId; // hardcoded ID of the message type for (autofill rules) - BE quirks
        }

        await saveRule(data, type as number, closeSingleRuleDialog);
    }, [closeSingleRuleDialog, saveRule, rule, type, hasTemporaryEmailTemplate]);

    const onBack = useCallback(() => {
        if (isRuleChanged || isAIRule) {
            showFormClientSideMessage(session, priority.netitems.shared.Message.Type.Warning, {
                title: texts.go_back_dialog_title,
                content: texts.go_back_dialog_content,
                confirmButtonText: texts.go_back_dialog_primary_btn,
                cancleButtonText: texts.go_back_dialog_secondary_btn,
                onConfirm: closeSingleRuleDialog
            });
        } else {
            closeSingleRuleDialog();
        }
    }, [session, isRuleChanged, isAIRule]);

    return {
        state: {
            isRuleActive,
            conditionOperator,
            conditions,
            hasTemporaryEmailTemplate
        },
        handlers: {
            addNewCondition,
            onChangeCondition,
            removeCondition,
            toggleActive,
            toggleConditionOperator,
            handleRuleFormData,
            onRuleSave,
            onBack,
            toggleHasTemporaryEmailTemplate
        }
    };
};
